import { AxiosResponse } from "axios";
import { useFormik } from "formik";
import { FC, useCallback, useContext, useEffect, useMemo } from "react";
import * as yup from "yup";
import {
  Autocomplete,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { styled } from "@mui/material/styles";
import { Clear } from "@mui/icons-material";
import { useShallow } from "zustand/shallow";

import { Dialog } from "components/Dialog";
import { API_URL_KPIS } from "constants/api_urls";
import { AxiosContext } from "contexts/AxiosContext";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { MultilineTextField } from "components/MultilineTextField";
import { AnnualKpi, BulkAnnualKpi } from "interfaces/kpis";
import { Fieldset } from "components/Fieldset/Fieldset";
import {
  useKpisCategoriesQuery,
  useKpisChecklistsQuery,
  useKpisMachinesWithActivityQuery,
  useKpisSensorGroupsQuery,
  useKpiWorkplansQuery
} from "hooks/queries/kpis";
import ProgressIndicatorContainer from "components/ProgressIndicatorContainer";
import { ListBuilder } from "pages/Kpis/KpisDialogs/ListBuilder";
import { useSitesQuery } from "hooks/queries/useSitesQuery";
import { useListBuilderStore } from "stores/list-builder.store";
import { Dropdown } from "components/Dropdown";
import { compareChecklistsArchivedLast } from "./utils";

export interface CreateAnnualKpiDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const YEAR_LENGTH = 4;
const NAME_MIN_LENGTH = 3;
const NAME_MAX_LENGTH = 500;
const DESCRIPTION_MAX_LENGTH = 2000;

const validationSchema = yup.object({
  name: yup
    .string()
    .nullable()
    .required("Name is required")
    .trim()
    .min(NAME_MIN_LENGTH, `Name must be at least ${NAME_MIN_LENGTH} characters`)
    .max(
      NAME_MAX_LENGTH,
      `Name must be less than ${NAME_MAX_LENGTH} characters`
    ),
  activityType: yup
    .string()
    .oneOf(["maintenance", "inspection"])
    .label("Activity Type")
    .required("Activity Type is required"),
  type: yup
    .string()
    .oneOf(["field_pro", "fleet_monitor"])
    .label("Type")
    .required("Type is required"),
  category: yup
    .number()
    .integer()
    .positive()
    .min(1, "Category is required"),
  year: yup
    .string()
    .label("Year")
    .required("Year is required")
    .trim()
    .matches(/^[0-9]+$/, `Year must be a valid ${YEAR_LENGTH} digit year`)
    .min(YEAR_LENGTH, `Year must be a ${YEAR_LENGTH} digit year`)
    .max(YEAR_LENGTH, `Year must be a ${YEAR_LENGTH} digit year`),
  checklists: yup
    .array()
    .of(yup.number().integer())
    .test(function() {
      const { activityType, type, workplans } = this.parent;
      if (
        type === "field_pro" &&
        activityType === "maintenance" &&
        workplans.length === 0 &&
        this.parent[this.path].length === 0
      ) {
        return this.createError({
          message:
            "Creating FieldPro Maintenance KPIs requires Checklists or Workplans"
        });
      }
      return true;
    })
    .label("Checklists"),
  workplans: yup
    .array()
    .of(yup.number().integer())
    .test(function() {
      const { checklists, activityType, type } = this.parent;
      if (
        type === "field_pro" &&
        activityType === "maintenance" &&
        checklists.length === 0 &&
        this.parent[this.path].length === 0
      ) {
        return this.createError({
          message:
            "Creating FieldPro Maintenance KPIs requires Checklists or Workplans"
        });
      }
      return true;
    })
    .label("Workplans"),
  sensorGroups: yup
    .array()
    .of(yup.number().integer())
    .label("Sensor Groups"),
  description: yup
    .string()
    .trim()
    .label("Description")
    .max(
      DESCRIPTION_MAX_LENGTH,
      `Description must be less than ${DESCRIPTION_MAX_LENGTH} characters`
    )
});

interface KpisTargetsBulkCreatePayload {
  kpis: number[];
  machines: number[];
  override_justification?: string;
}

interface FormValues {
  activityType: string;
  category: number;
  checklists: number[];
  description: string;
  name: string;
  sensorGroups: number[];
  type: string;
  workplans: number[];
  year: number;
  is_overdue: boolean;
}

interface CreateKpiMutationArgs extends FormValues {}

const activityTypes = [
  { label: "Maintenance", value: "maintenance" },
  { label: "Inspection", value: "inspection" }
];

const kpiTypes = [
  { label: "FieldPRO", value: "field_pro" },
  { label: "FleetMonitor", value: "fleet_monitor" }
];

const initialValues: FormValues = {
  name: "",
  activityType: activityTypes[0].value,
  type: kpiTypes[0].value,
  sensorGroups: [],
  description: "",
  category: -1,
  checklists: [],
  workplans: [],
  year: new Date().getFullYear(),
  is_overdue: false
};

type FormValuesType = typeof initialValues;

interface SiteOption {
  label: string;
  id: string;
}

export const CreateAnnualKpiDialog: FC<CreateAnnualKpiDialogProps> = ({
  isOpen,
  onClose,
  onConfirm
}): JSX.Element => {
  const { axios } = useContext(AxiosContext)!;
  const queryClient = useQueryClient();

  const { filterValue, selected, setFilterValue } = useListBuilderStore(
    useShallow(store => ({
      filterValue: store.filterValue,
      selected: store.selected,
      setFilterValue: store.setFilterValue
    }))
  );

  const selectedMachineIds = useMemo(() => {
    return selected.map(item => item.id);
  }, [selected]);

  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);

  const formik = useFormik<FormValues>({
    initialValues,
    validationSchema,
    onSubmit: values => {
      handleSubmit(formik.isValid, values, () => {});
    }
  });

  const machineActivityType = useMemo(() => {
    if (formik.values.type === "fleet_monitor") {
      return "fleetmonitor";
    }
    if (formik.values.activityType === "maintenance") {
      return "maintenance";
    }
    return "inspection";
  }, [formik.values.activityType, formik.values.type]);

  const {
    data: machines,
    isLoading: isMachinesLoading,
    isError: isMachinesError
  } = useKpisMachinesWithActivityQuery(
    {
      page: 1,
      size: 999_999,
      checklists: formik.values.checklists,
      workplans:
        formik.values.workplans.length > 0
          ? formik.values.workplans
          : undefined,
      sensor_groups: formik.values.sensorGroups
    },
    machineActivityType
  );

  const {
    data: availableWorkplans,
    isLoading: isWorkplansLoading
  } = useKpiWorkplansQuery({
    page: 1,
    size: 999_999,
    checklist: formik.values.checklists
  });

  const { data: sites, isLoading: isSitesLoading } = useSitesQuery();

  const siteOptions = useMemo(
    () =>
      sites?.results.map(site => ({
        label: site.name,
        id: site.name
      })) ?? [],
    [sites]
  );

  const {
    data: categories,
    isLoading: isCategoriesLoading
  } = useKpisCategoriesQuery({ page: 1, size: 999_999 });

  const categoryOptions = useMemo(
    () =>
      categories?.results.map(category => ({
        label: category.name,
        id: category.id
      })) ?? [],
    [categories]
  );

  const workplanOptions = useMemo(
    () =>
      availableWorkplans?.results.map(workplan => ({
        label: workplan.name,
        checklist: workplan.checklist,
        id: workplan.id
      })) ?? [],
    [availableWorkplans]
  );

  const {
    data: checklists,
    isLoading: isChecklistsLoading
  } = useKpisChecklistsQuery({ page: 1, size: 999_999 });

  const checklistOptions = useMemo(
    () =>
      checklists?.results
        .sort(compareChecklistsArchivedLast)
        .map(checklist => ({
          label:
            checklist.status === "archived"
              ? `(ARCHIVED) ${checklist.name}`
              : checklist.name,
          id: checklist.id
        })) ?? [],
    [checklists]
  );

  const {
    data: sensorGroups,
    isLoading: isSensorGroupsLoading
  } = useKpisSensorGroupsQuery({ page: 1, size: 999_999 });

  const sensorGroupOptions = useMemo(
    () =>
      sensorGroups?.results.map(sensorGroup => ({
        label: sensorGroup.name,
        id: sensorGroup.id
      })) ?? [],
    [sensorGroups]
  );

  const attachMachinesToKpiMutation = useMutation({
    mutationFn: async (payload: KpisTargetsBulkCreatePayload) => {
      return axios.post<AnnualKpi, AxiosResponse<AnnualKpi>>(
        `${API_URL_KPIS}/targets/bulk_create/`,
        payload
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(["kpis", "progress"]);
      await queryClient.invalidateQueries(["kpis", "targets"]);
      onClose();
    },
    onError: (err: Error) => {
      console.info(err);

      addSnackbarToQueue({
        message: "Failed to attach machines to KPI.",
        severity: "error"
      });
    }
  });

  const createAnnualKpiMutation = useMutation({
    mutationFn: async (newAnnualKpi: CreateKpiMutationArgs) => {
      const {
        name,
        type,
        activityType,
        description,
        category,
        checklists,
        sensorGroups,
        workplans,
        year
      } = newAnnualKpi;

      let endpointUrl = `${API_URL_KPIS}/fieldpro_inspection_kpis/`;
      if (type === "field_pro") {
        if (activityType === "maintenance") {
          endpointUrl = `${API_URL_KPIS}/maintenance_kpis/bulk_create_per_workplan/`;
        } else if (activityType === "inspection") {
          endpointUrl = `${API_URL_KPIS}/fieldpro_inspection_kpis/`;
        }
      }

      if (type === "fleet_monitor") {
        endpointUrl = `${API_URL_KPIS}/fleetmonitor_measurement_kpis/`;
      }

      const payload = {
        name,
        description,
        category,
        year,
        checklists: [],
        sensor_groups: []
      };

      if (type === "field_pro") {
        // @ts-ignore
        payload["checklists"] = checklists;

        if (activityType === "maintenance") {
          // @ts-ignore
          payload["workplans"] = workplans;
        }
      }

      if (type === "fleet_monitor") {
        // @ts-ignore
        payload["sensor_groups"] = sensorGroups;
      }

      return axios.post<AnnualKpi, AxiosResponse<AnnualKpi | BulkAnnualKpi>>(
        endpointUrl,
        payload
      );
    },
    onSuccess: async res => {
      addSnackbarToQueue({
        message:
          "id" in res.data || res.data.created.length < 2
            ? "New KPI was successfully created."
            : `${res.data.created.length} KPI(s) successfully created.`,
        severity: "success"
      });

      attachMachinesToKpiMutation.mutate({
        kpis:
          "id" in res.data
            ? [res.data.id]
            : res.data.created.map(kpi => kpi.id),
        machines: selectedMachineIds
      });
    },
    onError: () => {
      addSnackbarToQueue({
        message: "Failed to create new KPI.",
        severity: "error"
      });
    }
  });

  useEffect(() => {
    if (formik.values.activityType === "maintenance") {
      formik.setFieldValue("type", "field_pro");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.activityType]);

  const handleSubmit = useCallback(
    async (
      isValid: boolean,
      values: FormValuesType,
      reset: () => void
    ): Promise<void> => {
      if (
        !isValid ||
        isMachinesLoading ||
        isMachinesError ||
        isCategoriesLoading ||
        isChecklistsLoading ||
        isSensorGroupsLoading
      ) {
        return;
      }
      await createAnnualKpiMutation.mutate(values);
      onConfirm();
      reset();
    },
    [
      createAnnualKpiMutation,
      isCategoriesLoading,
      isMachinesError,
      isMachinesLoading,
      isChecklistsLoading,
      isSensorGroupsLoading,
      onConfirm
    ]
  );

  const closeAndResetDialog = (): void => {
    onClose();

    setTimeout(() => {
      formik.setErrors({});
      formik.resetForm();
    }, 50);
  };

  // Reset form when dialog is opened.
  useEffect(() => {
    if (isOpen) {
      formik.setErrors({});
      formik.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loading =
    createAnnualKpiMutation.isLoading || attachMachinesToKpiMutation.isLoading;

  const shouldDisableConfirm =
    !formik.isValid || (!!Object.keys(formik.touched).length && !formik.dirty);

  const handleChangeSelectedSite = (site: SiteOption | null): void => {
    // Only pass an object of key-value pairs for filtering
    // If site is selected, filter by site_name; otherwise, clear filter
    setFilterValue(site ? { site_name: site.id } : {});
  };

  const MemoizedListBuilder = useMemo(() => {
    return (
      <ListBuilder
        availableItems={machines?.results ?? []}
        selectedItems={[]}
        disabled={isMachinesLoading || isMachinesError}
      />
    );
  }, [machines?.results, isMachinesLoading, isMachinesError]);

  return (
    <Dialog
      dialogId="create-annualkpi-dialog"
      title="Create KPI"
      open={isOpen}
      size="large"
      fullWidth={true}
      onClose={closeAndResetDialog}
      data-qa="KPI-Create-AnnualKpi-Dialog"
      confirmDisabled={loading || shouldDisableConfirm}
      confirmText={
        formik.values.workplans.length < 2
          ? "Create"
          : `Create ${formik.values.workplans.length} KPIs`
      }
      onSubmit={event => {
        event.preventDefault();
        formik.submitForm();
      }}
    >
      <StyledDialogContent>
        <TextField
          variant="outlined"
          fullWidth
          autoFocus
          name="name"
          required
          id="name"
          label="Name"
          data-qa="KPI-Text-Name"
          inputProps={{ maxLength: NAME_MAX_LENGTH }}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
          value={formik.values.name}
        />

        <Dropdown
          fullWidth
          id="activityType"
          name="activityType"
          label="Activity Type"
          required
          value={formik.values.activityType}
          onChange={event => {
            formik.handleChange(event);
            formik.setFieldValue("is_overdue", false);
          }}
          onBlur={formik.handleBlur}
          data-qa="KPI-Select-KPI-Activity-Type"
          error={
            formik.touched.activityType && Boolean(formik.errors.activityType)
          }
          helperText={
            formik.touched.activityType && formik.errors.activityType
              ? formik.errors.activityType
              : ""
          }
        >
          {activityTypes.map(({ label, value }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          )) ?? []}
        </Dropdown>

        <Dropdown
          disabled={formik.values.activityType === "maintenance"}
          fullWidth
          id="type"
          name="type"
          label="Inspection Type"
          required
          value={formik.values.type}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          data-qa="KPI-Select-KPI-Inspection-Type"
          error={formik.touched.type && Boolean(formik.errors.type)}
          helperText={
            formik.touched.type && formik.errors.type ? formik.errors.type : ""
          }
        >
          {kpiTypes.map(({ label, value }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          )) ?? []}
        </Dropdown>

        <Fieldset>
          <legend>Link to…</legend>

          <Stack gap={2}>
            <Autocomplete
              data-qa="KPI-Select-Sensor-Groups"
              multiple
              loading={isSensorGroupsLoading}
              loadingText="Loading sensor groups…"
              options={sensorGroupOptions}
              getOptionLabel={option => option.label}
              disabled={formik.values.type !== "fleet_monitor"}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Sensor Groups"
                  name="sensorGroups"
                  error={
                    formik.touched.sensorGroups &&
                    Boolean(formik.errors.sensorGroups)
                  }
                  helperText={
                    formik.touched.sensorGroups && formik.errors.sensorGroups
                  }
                />
              )}
              onChange={(_event, values) => {
                formik.setFieldValue(
                  "sensorGroups",
                  values.map(sensorGroup => sensorGroup.id)
                );
              }}
              onBlur={formik.handleBlur}
            />

            <Autocomplete
              data-qa="KPI-Select-Checklists"
              multiple
              loading={isChecklistsLoading}
              loadingText="Loading checklists…"
              options={checklistOptions}
              getOptionLabel={option => option.label}
              disabled={
                formik.values.type !== "field_pro" ||
                formik.values.workplans.length > 0
              }
              isOptionEqualToValue={(option, value) => option.id === value.id} // handle dupes
              renderInput={params => (
                <TextField
                  {...params}
                  label="Checklists"
                  name="checklists"
                  error={
                    formik.touched.checklists &&
                    Boolean(formik.errors.checklists)
                  }
                  helperText={
                    formik.touched.checklists && formik.errors.checklists
                  }
                />
              )}
              // handle dupes
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.label}
                </li>
              )}
              onChange={(_event, values) => {
                formik.setFieldValue(
                  "checklists",
                  values.map(checklist => checklist.id)
                );
              }}
              onBlur={formik.handleBlur}
            />

            <Autocomplete
              data-qa="KPI-Select-Workplans"
              multiple
              loading={isWorkplansLoading}
              loadingText="Loading workplans…"
              options={workplanOptions}
              getOptionLabel={option => option.label}
              disabled={
                formik.values.activityType === "inspection" ||
                formik.values.checklists.length < 1
              }
              isOptionEqualToValue={(option, value) => option.id === value.id} // handle dupes
              renderInput={params => (
                <TextField
                  {...params}
                  label="Workplans"
                  name="workplans"
                  error={
                    formik.touched.workplans && Boolean(formik.errors.workplans)
                  }
                  helperText={
                    formik.touched.workplans && formik.errors.workplans
                  }
                />
              )}
              // handle dupes
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.label}
                </li>
              )}
              onChange={(_event, values) => {
                formik.setFieldValue(
                  "workplans",
                  values.map(workplan => workplan.id)
                );
                if (values.length > 0) {
                  formik.setFieldValue(
                    "checklists",
                    values.map(workplan => workplan.checklist)
                  );
                }
                if (values.length < 1) {
                  formik.setFieldValue("is_overdue", false);
                }
              }}
              onBlur={formik.handleBlur}
            />
          </Stack>
        </Fieldset>

        {/* TODO: make creatable: https://mui.com/material-ui/react-autocomplete/#creatable */}
        <Autocomplete
          loading={isCategoriesLoading}
          loadingText="Loading categories…"
          options={categoryOptions}
          getOptionLabel={option => option.label}
          renderInput={params => (
            <TextField
              {...params}
              label="Category"
              name="category"
              required
              error={formik.touched.category && Boolean(formik.errors.category)}
              helperText={formik.touched.category && formik.errors.category}
            />
          )}
          onChange={(_event, value) => {
            formik.setFieldValue("category", value?.id);
          }}
          onBlur={formik.handleBlur}
        />

        <TextField
          variant="outlined"
          fullWidth
          name="year"
          required
          id="year"
          label="Year"
          data-qa="KPI-Text-Year"
          inputProps={{ minLength: 4, maxLength: 4 }}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.year && Boolean(formik.errors.year)}
          helperText={formik.touched.year && formik.errors.year}
          value={formik.values.year}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear"
                  onClick={() =>
                    formik.setFieldValue("year", new Date().getFullYear())
                  }
                >
                  <Clear />
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        <MultilineTextField
          name="description"
          id="projectDescription"
          label="Description"
          value={formik.values.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            (formik.dirty || formik.touched.description) &&
            Boolean(formik.errors.description)
          }
          helperText={
            (formik.dirty || formik.touched.description) &&
            formik.errors.description
          }
          data-qa="KPI-Text-Description"
          fullWidth
        />

        <Fieldset>
          <legend>Machines</legend>
          <ProgressIndicatorContainer
            loading={isMachinesLoading}
            error={isMachinesError}
            errorMessage="Failed to load content…"
          >
            <Stack gap={1}>
              <Stack gap={3} direction="row">
                <Autocomplete
                  fullWidth
                  loading={isSitesLoading}
                  loadingText="Loading sites…"
                  options={siteOptions}
                  getOptionLabel={option => option.label}
                  renderInput={params => (
                    <TextField {...params} label="Sites" name="sites" />
                  )}
                  onChange={(_, value) => {
                    if (!value) {
                      const newFilterValue = { ...filterValue };
                      delete newFilterValue.site_name;
                      setFilterValue(newFilterValue);
                    }
                    handleChangeSelectedSite(value);
                  }}
                />

                <FormControlLabel
                  disabled={
                    formik.values.workplans.length < 1 ||
                    formik.values.activityType !== "maintenance"
                  }
                  label="Show overdue only"
                  sx={{ whiteSpace: "nowrap" }}
                  control={
                    <Checkbox
                      name="is_overdue"
                      checked={formik.values.is_overdue}
                      onChange={event => {
                        formik.setFieldValue(
                          "is_overdue",
                          event.target.checked ? true : null
                        );
                        setFilterValue({
                          // @ts-ignore
                          is_overdue: event.target.checked ? true : null
                        });
                      }}
                    />
                  }
                />
              </Stack>

              {MemoizedListBuilder}
            </Stack>
          </ProgressIndicatorContainer>
        </Fieldset>
      </StyledDialogContent>
    </Dialog>
  );
};

const StyledDialogContent = styled("div")`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  gap: ${props => props.theme.spacing(2)};
`;
StyledDialogContent.displayName = "StyledDialogContent";
